/**
 * AttachmentPicker — a polished modal for adding an attachment to a message.
 * Three tabs: Upload (file dialog), Media library, Paste URL.
 *
 * Returns an attachment descriptor: { kind, url, name, content_type?, size_bytes? }
 *   kind = "upload" | "media" | "url"
 */
import React, { useEffect, useRef, useState } from "react";
import { X, Upload, Image as ImageIcon, Link as LinkIcon, Loader2, FileText } from "lucide-react";
import { api } from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export function AttachmentPicker({ open, onClose, onPick, isClient = false }) {
  const [tab, setTab] = useState("upload");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [media, setMedia] = useState(null);
  const [urlValue, setUrlValue] = useState("");
  const [urlName, setUrlName] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTab("upload"); setBusy(false); setError(""); setUrlValue(""); setUrlName("");
      return;
    }
    if (tab === "library" && !media) {
      api.get("/cms/media?media_type=image").then(({ data }) => setMedia(Array.isArray(data) ? data : []))
        .catch(() => setMedia([]));
    }
  }, [open, tab, media]);

  if (!open) return null;

  const handleUpload = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const path = isClient ? "/client-portal/concierge/upload" : "/concierge/upload";
      const { data } = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onPick({ kind: "upload", url: data.url, name: data.name, content_type: data.content_type, size_bytes: data.size_bytes });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleUrlPick = () => {
    const u = urlValue.trim();
    if (!u) return;
    onPick({ kind: "url", url: u, name: urlName.trim() || u });
    onClose();
  };

  const handleLibraryPick = (item) => {
    onPick({
      kind: "media",
      url: `/api/cms/media/file/${item.id}`,
      name: item.original_filename || item.alt_text || "Attachment",
      content_type: item.content_type,
      size_bytes: item.size_bytes,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="attachment-picker">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E4DC]">
          <h3 className="font-serif text-lg text-[#1A1A1A]">Add attachment</h3>
          <button onClick={onClose} className="p-1 hover:bg-[#FAFAF8] rounded" type="button" data-testid="attachment-close">
            <X size={16} />
          </button>
        </div>
        <div className="flex border-b border-[#E8E4DC]">
          {[
            { id: "upload", label: "Upload", icon: Upload },
            ...(isClient ? [] : [{ id: "library", label: "Media library", icon: ImageIcon }]),
            { id: "url", label: "Paste URL", icon: LinkIcon },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm transition-colors inline-flex items-center gap-2 ${tab === t.id ? "text-[#1A1A1A] border-b-2 border-[#C9A961] -mb-px" : "text-[#6B6B6B] hover:text-[#1A1A1A]"}`}
              type="button"
              data-testid={`attachment-tab-${t.id}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-auto flex-1">
          {error && (
            <div className="mb-3 px-3 py-2 rounded bg-[#FCEAE0] text-[#C9622D] text-sm" data-testid="attachment-error">{error}</div>
          )}

          {tab === "upload" && (
            <div>
              <p className="text-xs text-[#6B6B6B] mb-3">
                Documents (PDF, DOCX, TXT, CSV) or images (JPG, PNG, WebP, GIF). Up to 25MB.
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                className="w-full h-32 border border-dashed border-[#E8E4DC] rounded-lg flex flex-col items-center justify-center gap-2 text-sm text-[#6B6B6B] hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-colors disabled:opacity-50"
                type="button"
                data-testid="attachment-upload-btn"
              >
                {busy ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                {busy ? "Uploading…" : "Click to choose a file"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,.doc,.docx,.txt,.csv"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                data-testid="attachment-file-input"
              />
            </div>
          )}

          {tab === "library" && !isClient && (
            media === null ? (
              <div className="text-center py-12 text-sm text-[#999]">Loading library…</div>
            ) : media.length === 0 ? (
              <div className="text-center py-12 text-sm text-[#999]">No images uploaded yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {media.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleLibraryPick(item)}
                    className="aspect-square overflow-hidden rounded border border-[#E8E4DC] hover:border-[#1A1A1A] transition-colors"
                    type="button"
                    data-testid={`attachment-lib-${item.id}`}
                  >
                    <img src={`${API}${item.file_url}`} alt={item.original_filename} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )
          )}

          {tab === "url" && (
            <div>
              <p className="text-xs text-[#6B6B6B] mb-3">
                Paste any direct link — a document URL, an image, a shared resource.
              </p>
              <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">URL</label>
              <input
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                placeholder="https://example.com/document.pdf"
                autoFocus
                className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A] mb-3"
                data-testid="attachment-url-input"
              />
              <label className="block text-[11px] uppercase tracking-[0.15em] text-[#999] mb-1.5">Display name (optional)</label>
              <input
                value={urlName}
                onChange={(e) => setUrlName(e.target.value)}
                placeholder="Itinerary PDF"
                className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded outline-none focus:border-[#1A1A1A] mb-4"
                data-testid="attachment-url-name"
              />
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A]" type="button">
                  Cancel
                </button>
                <button
                  onClick={handleUrlPick}
                  disabled={!urlValue.trim()}
                  className="px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50"
                  type="button"
                  data-testid="attachment-url-add"
                >
                  Add link
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
